"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="115949" className="hidden lg:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 bg-muted animate-pulse rounded-2xl" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("group inline-flex h-9 w-max items-center justify-center rounded-xl bg-transparent px-4 py-2 text-base font-medium transition-all duration-300", "hover:text-secondary focus:text-secondary focus:outline-none disabled:pointer-events-none disabled:opacity-50", "relative after:absolute after:bottom-1.5 after:left-4 after:right-4 after:h-[3px] after:bg-secondary after:rounded-full after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-center hover:-translate-y-0.5")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="lg:hidden absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl animate-in slide-in-from-top-2 rounded-b-3xl">
      <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-xl font-medium py-3 border-b border-border/50 text-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)} data-index={i}>
            {item.label}
          </Link>)}
        <div className="pt-4">
          <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-lg font-bold shadow-lg shadow-primary/20 animate-pulse">
            Book a Free Trial
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="navigation-hub" className={cn("sticky top-0 z-50 w-full transition-all duration-500 ease-in-out border-b", isScrolled ? "h-18 bg-background/80 backdrop-blur-lg border-border shadow-md rounded-b-[2rem]" : "h-24 bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex-shrink-0 flex items-center z-50 transition-transform duration-300 hover:scale-105">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/cova_tree_logo.png" alt="LinguaKids Logo" className="h-10 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="137">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex flex-shrink-0 items-center">
          <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all duration-300 px-8 py-6 text-base font-bold shadow-lg shadow-primary/25">
            Book a Free Trial
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="lg:hidden hover:bg-accent/50 rounded-full" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-7 text-primary" /> : <Menu className="size-7 text-primary" />}
        </Button>
      </div>

      {}
      <MenuProvider menu_id="137">
        <MobileMenuConsumer isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </header>;
}